/**
 * PaymentTermsSelect — the single, shared control for choosing payment terms
 * anywhere in the ERP cluster. Pick a tenant-configured preset, or "Custom…"
 * to set a normalized (basis, days) value inline. It NEVER accepts free text.
 *
 * Emits a {@link PaymentTermsValue}: `payment_terms_id` (the chosen preset, or
 * null for a custom override) plus the resolved `basis` and `days`.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { LocalizedNumericInput } from '../components/LocalizedNumericInput';
import { usePaymentTerms } from './usePaymentTerms';
import { PAYMENT_TERMS_BASES, type PaymentTermsBasis, type PaymentTermsValue } from './types';

const CUSTOM_SENTINEL = '__custom__';

interface PaymentTermsSelectProps {
  value: PaymentTermsValue;
  onChange: (value: PaymentTermsValue) => void;
  /** Allow the "Custom…" option for an ad-hoc per-document/per-customer override. */
  allowCustom?: boolean;
  disabled?: boolean;
  /** Render without the trigger border, for inline detail-page editing. */
  id?: string;
  className?: string;
}

export function PaymentTermsSelect({
  value,
  onChange,
  allowCustom = true,
  disabled = false,
  id,
  className,
}: PaymentTermsSelectProps) {
  const { t } = useTranslation('common');
  const { data: terms = [], isLoading } = usePaymentTerms(true);

  const isCustom = !value.payment_terms_id && value.payment_terms_basis != null;
  const selectValue = value.payment_terms_id ?? (isCustom ? CUSTOM_SENTINEL : '');

  const sortedTerms = useMemo(
    () => [...terms].sort((a, b) => a.sort_order - b.sort_order),
    [terms],
  );

  const handleSelect = (selected: string) => {
    if (selected === CUSTOM_SENTINEL) {
      onChange({
        payment_terms_id: null,
        payment_terms_basis: value.payment_terms_basis ?? 'net',
        payment_terms_days: value.payment_terms_days ?? 30,
      });
      return;
    }
    const preset = sortedTerms.find((p) => p.id === selected);
    if (preset) {
      onChange({
        payment_terms_id: preset.id,
        payment_terms_basis: preset.basis,
        payment_terms_days: preset.net_days,
      });
    }
  };

  const handleBasisChange = (basis: PaymentTermsBasis) => {
    onChange({
      payment_terms_id: null,
      payment_terms_basis: basis,
      payment_terms_days: basis === 'due_on_receipt' ? 0 : (value.payment_terms_days ?? 30),
    });
  };

  const handleDaysChange = (days: number | null) => {
    onChange({
      payment_terms_id: null,
      payment_terms_basis: value.payment_terms_basis ?? 'net',
      payment_terms_days: days,
    });
  };

  return (
    <div className={className}>
      <Select value={selectValue} onValueChange={handleSelect} disabled={disabled || isLoading}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={t('paymentTerms.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {sortedTerms.map((term) => (
            <SelectItem key={term.id} value={term.id}>
              {term.name}
            </SelectItem>
          ))}
          {allowCustom && (
            <SelectItem value={CUSTOM_SENTINEL}>{t('paymentTerms.custom')}</SelectItem>
          )}
        </SelectContent>
      </Select>

      {isCustom && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div className="w-full sm:w-44">
            <Select
              value={value.payment_terms_basis ?? 'net'}
              onValueChange={(v) => handleBasisChange(v as PaymentTermsBasis)}
              disabled={disabled}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_TERMS_BASES.map((basis) => (
                  <SelectItem key={basis} value={basis}>
                    {t(`paymentTerms.basisLabels.${basis}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {value.payment_terms_basis !== 'due_on_receipt' && (
            <div className="w-full sm:w-32">
              <LocalizedNumericInput
                value={value.payment_terms_days ?? null}
                onChange={handleDaysChange}
                min={0}
                decimalPlaces={0}
                allowNegative={false}
                size="default"
                fullWidth
                placeholder={t('paymentTerms.daysPlaceholder')}
                disabled={disabled}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
